
import React, { useState, useEffect } from "react";
import FeedbackForm from "./FeedbackForm";
import FilterControls from "./list/FilterControls";
import FeedbackMessages from "./list/FeedbackMessages";
import { FeedbackMessage as FeedbackMessageType, FeedbackCategoryTag } from "@/types/feedback";
import { useFeedbackMessages } from "@/hooks/useFeedbackMessages";
import { toast } from "sonner";
import { usePermissions } from "@/context/PermissionsContext";

const FeedbackList: React.FC = () => {
  const { 
    messages, 
    isLoading, 
    createMessage,
    user 
  } = useFeedbackMessages();
  
  const { hasPermission } = usePermissions();
  const canWriteFeedback = hasPermission("feedback", "write");
  
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<FeedbackCategoryTag>(null);
  const [replyToId, setReplyToId] = useState<string | null>(null);
  const [filteredMessages, setFilteredMessages] = useState<FeedbackMessageType[]>([]);

  useEffect(() => {
    if (!messages) return;

    let filtered = [...messages];
    
    if (searchTerm) {
      filtered = filtered.filter(
        (msg) => 
          msg.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
          msg.username.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (categoryFilter) {
      filtered = filtered.filter(msg => msg.category_tag === categoryFilter);
    }
    
    setFilteredMessages(filtered);
  }, [messages, searchTerm, categoryFilter]);

  const handleSubmitMessage = (
    message: string, 
    category: FeedbackCategoryTag, 
    attachment?: string | null
  ) => {
    if (!user) {
      console.error("User not logged in when trying to submit message");
      toast.error("Je moet ingelogd zijn om een bericht te plaatsen");
      return;
    }
    
    if (!canWriteFeedback) {
      toast.error("Je hebt onvoldoende rechten om berichten te plaatsen");
      return;
    }
    
    console.log("Submitting message as user:", user.id, user.name || user.username);
    
    createMessage.mutate({
      message,
      user_id: user.id,
      username: user.username || user.name || "Gebruiker",
      parent_id: replyToId,
      category_tag: category,
      attachment_path: attachment,
    });
    
    if (replyToId) {
      setReplyToId(null);
    }
  };

  const handleReply = (parentId: string) => {
    if (!canWriteFeedback) {
      toast.error("Je hebt onvoldoende rechten om te reageren");
      return;
    }
    
    setReplyToId(parentId);
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }, 100);
  };

  return (
    <div className="space-y-6">
      {canWriteFeedback && (
        <FeedbackForm 
          onSubmit={handleSubmitMessage} 
          replyToId={replyToId}
          onCancelReply={() => setReplyToId(null)}
          isSubmitting={createMessage.isPending}
        />
      )}
      
      <FilterControls
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        categoryFilter={categoryFilter}
        setCategoryFilter={setCategoryFilter}
      />
      
      <FeedbackMessages
        messages={filteredMessages}
        isLoading={isLoading}
        onReply={handleReply}
        hasFilters={!!searchTerm || !!categoryFilter}
      />
    </div>
  );
};

export default FeedbackList;
